import { FC, ReactNode, useMemo } from 'react'

import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { campaignsList } from 'src/Router/routes'
import { ScheduleSteps } from 'src/Types/ui'

import { Breadcrumb } from '../Breadcrumb'

import { useStepSync } from './useStepSync'

export { ScheduleSteps }

export interface PrimaryScheduleSteps {
  path: string
  title: string
}

interface ScheduleTabsProps {
  steps: PrimaryScheduleSteps[]
  children: [ReactNode, ReactNode] | [ReactNode, ReactNode, ReactNode]
  defaultStep?: ScheduleSteps
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const BreadCrumbWrapper = styled.div`
  display: flex;
`

const BACK_INDEX = -1
const LENGTH_OF_STEPS_WITH_TYPE = 3

export const PrimaryScheduleTabs: FC<ScheduleTabsProps> = ({
  children,
  steps,
  defaultStep = ScheduleSteps.SETTINGS,
}) => {
  const navigate = useNavigate()
  const { search, setSearch } = useCustomSearchParams()
  const currentStep = search.get('step') as ScheduleSteps

  const STEPS: PrimaryScheduleSteps[] = useMemo(() => {
    const currentSteps = [...steps]
    const stepIndex = currentSteps.findIndex((s) =>
      s.path.includes(`step=${currentStep}`),
    )
    if (stepIndex !== BACK_INDEX) {
      return currentSteps.slice(0, stepIndex + 1)
    }
    return currentSteps.slice(0, 1)
  }, [currentStep, steps])

  const handleGoBack = (): void => {
    if (currentStep === ScheduleSteps.CHECKOUT) {
      setSearch({ step: ScheduleSteps.SETTINGS })
      return
    }
    if (currentStep === ScheduleSteps.SETTINGS) {
      const hasTypeStep = steps.some((s) =>
        s.path.includes(`step=${ScheduleSteps.TYPE}`),
      )
      if (hasTypeStep) {
        setSearch({ step: ScheduleSteps.TYPE })
        return
      }
    }
    navigate(campaignsList)
  }

  const MAP_STEPS = useMemo(() => {
    if (children.length === LENGTH_OF_STEPS_WITH_TYPE) {
      return {
        [ScheduleSteps.TYPE]: children[0],
        [ScheduleSteps.SETTINGS]: children[1],
        [ScheduleSteps.CHECKOUT]: children[2],
      }
    }
    return {
      [ScheduleSteps.SETTINGS]: children[0],
      [ScheduleSteps.CHECKOUT]: children[1],
    }
  }, [children])

  useStepSync({ defaultStep, search, setSearch, mapSteps: MAP_STEPS })

  return (
    <Container>
      <BreadCrumbWrapper>
        <Breadcrumb items={STEPS} onButtonClick={handleGoBack} />
      </BreadCrumbWrapper>
      {MAP_STEPS[currentStep] || children[0]}
    </Container>
  )
}
